/**
 * Member Share & QR referral — visitor-side capture.
 *
 * A share code arriving on any Vitolier URL (`?ref=VTLR-XXXXXX`) is held
 * locally so it survives the full journey: landing → sign-up → email
 * verification → onboarding → referral step. The stored code is never
 * trusted on its own: the server re-validates it when the attribution is
 * written, and rejects revoked codes, ineligible referrers and self-referral.
 *
 * Nothing here grants rewards, benefits, permissions or membership changes.
 */
import { normaliseReferralToken, type MemberReferralEntryMethod } from "@/lib/referral/tokens";

const KEY = "vitolier_member_referral";
const TTL_MS = 1000 * 60 * 60 * 24 * 30;

export interface CapturedReferral {
  token: string;
  entryMethod: MemberReferralEntryMethod;
  captured_at: number;
}

function write(payload: CapturedReferral) {
  const raw = JSON.stringify(payload);
  try { sessionStorage.setItem(KEY, raw); } catch { /* ignore */ }
  try { localStorage.setItem(KEY, raw); } catch { /* ignore */ }
}

export function readCapturedReferral(): CapturedReferral | null {
  const read = (s: Storage | null) => { try { return s?.getItem(KEY) ?? null; } catch { return null; } };
  const raw =
    read(typeof sessionStorage !== "undefined" ? sessionStorage : null) ??
    read(typeof localStorage !== "undefined" ? localStorage : null);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CapturedReferral;
    const token = normaliseReferralToken(parsed?.token);
    if (!token) return null;
    if (!parsed.captured_at || Date.now() - parsed.captured_at > TTL_MS) return null;
    return {
      token,
      entryMethod: parsed.entryMethod === "member_qr" ? "member_qr" : "member_share_link",
      captured_at: parsed.captured_at,
    };
  } catch {
    return null;
  }
}

export function clearCapturedReferral() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

export function captureReferral(token: string, entryMethod: MemberReferralEntryMethod): CapturedReferral | null {
  const norm = normaliseReferralToken(token);
  if (!norm) return null;
  const payload: CapturedReferral = { token: norm, entryMethod, captured_at: Date.now() };
  write(payload);
  return payload;
}

/**
 * Capture from a query string. The first code seen wins, so a later share
 * link cannot silently overwrite the referral a visitor actually arrived on.
 */
export function captureReferralFromSearch(search: string): CapturedReferral | null {
  let params: URLSearchParams;
  try { params = new URLSearchParams(search); } catch { return null; }
  const raw = params.get("ref") ?? params.get("referral");
  const norm = normaliseReferralToken(raw);
  if (!norm) return null;
  const existing = readCapturedReferral();
  if (existing) return existing;
  const qr = (params.get("src") ?? "").toLowerCase() === "qr";
  return captureReferral(norm, qr ? "member_qr" : "member_share_link");
}
